import { useEffect, useState } from 'react';
import { Mail, Clock, Users, Paperclip, CheckCircle2, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '@/foundation/altzero_base/base/utils/supabaseClient';
import { Card } from '@/foundation/altzero_base/base/components/ui/card';

type Recipient = {
  email: string;
  type: 'to' | 'cc' | 'bcc';
};

type Attachment = {
  filename: string;
  size: number;
  type: string;
};

type Communication = {
  id: string;
  sent_at: string;
  sent_from_email: string;
  recipients: Recipient[];
  subject: string;
  body_html: string;
  attachments: Attachment[];
  delivery_status: string;
};

type CommunicationHistoryProps = {
  tenderId: string;
};

export const CommunicationHistory = ({ tenderId }: CommunicationHistoryProps) => {
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    loadCommunications();
  }, [tenderId]);

  const loadCommunications = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('tender_communications')
        .select('*')
        .eq('tender_id', tenderId)
        .order('sent_at', { ascending: false });

      if (fetchError) throw fetchError;

      setCommunications(data || []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load communications';
      setError(msg);
      console.error('Failed to load communication history:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <Card className="p-8">
        <div className="flex flex-col items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500 mb-3" />
          <p className="text-sm text-gray-500">Loading communication history...</p>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <Mail className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-1">Failed to load communication history</h4>
            <p className="text-sm text-red-600">{error}</p>
            <button
              onClick={loadCommunications}
              className="mt-2 text-sm text-blue-600 hover:underline"
            >
              Try again
            </button>
          </div>
        </div>
      </Card>
    );
  }

  if (communications.length === 0) {
    return (
      <Card className="p-8">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-gray-400" />
          </div>
          <h4 className="font-medium text-gray-900 mb-1">No emails sent yet</h4>
          <p className="text-sm text-gray-500">
            Once you send the proposal to a client, the communication history will appear here.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Mail className="w-5 h-5 text-blue-600" />
          Communication History
          <span className="text-sm font-normal text-gray-500">({communications.length})</span>
        </h3>
      </div>

      <div className="space-y-3">
        {communications.map((comm) => {
          const toRecipients = comm.recipients.filter((r) => r.type === 'to');
          const ccRecipients = comm.recipients.filter((r) => r.type === 'cc');
          const isExpanded = expandedId === comm.id;

          return (
            <Card key={comm.id} className="p-5 hover:shadow-md transition-shadow">
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 truncate mb-1">{comm.subject}</h4>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">From: {comm.sent_from_email}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 ml-4 flex-shrink-0">
                  <div className="flex items-center gap-1.5 text-xs text-gray-500">
                    <Clock className="w-3.5 h-3.5" />
                    {formatDate(comm.sent_at)}
                  </div>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                    <CheckCircle2 className="w-3 h-3" />
                    {comm.delivery_status}
                  </span>
                </div>
              </div>

              {/* Recipients */}
              <div className="mb-3">
                <div className="flex items-start gap-2">
                  <Users className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap gap-1.5">
                      {toRecipients.map((recipient, idx) => (
                        <span
                          key={`to-${idx}`}
                          className="inline-flex items-center px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 text-xs font-medium"
                        >
                          <span className="font-semibold mr-1">To:</span>
                          {recipient.email}
                        </span>
                      ))}
                      {ccRecipients.map((recipient, idx) => (
                        <span
                          key={`cc-${idx}`}
                          className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 text-xs font-medium"
                        >
                          <span className="font-semibold mr-1">CC:</span>
                          {recipient.email}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Attachments */}
              {comm.attachments && comm.attachments.length > 0 && (
                <div className="pt-3 border-t border-gray-100">
                  <div className="flex items-start gap-2">
                    <Paperclip className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700 mb-1.5">
                        {comm.attachments.length} attachment{comm.attachments.length === 1 ? '' : 's'}
                      </p>
                      <ul className="space-y-1">
                        {comm.attachments.map((att, idx) => (
                          <li
                            key={idx}
                            className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1.5"
                          >
                            <span className="flex items-center gap-1.5 min-w-0 flex-1">
                              <Paperclip className="w-3 h-3 text-gray-400 flex-shrink-0" />
                              <span className="truncate font-medium text-gray-700">{att.filename}</span>
                            </span>
                            <span className="text-gray-500 ml-2 flex-shrink-0">
                              {formatFileSize(att.size)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Expandable Email Body */}
              {comm.body_html && (
                <>
                  <div className="pt-3 border-t border-gray-100 mt-3">
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : comm.id)}
                      className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                    >
                      {isExpanded ? (
                        <>
                          <ChevronUp className="w-4 h-4" />
                          Hide email content
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          View email content
                        </>
                      )}
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                        <div
                          className="prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: comm.body_html }}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};
